import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography
} from '@mui/material';

import TablePaginationActions from '../../../../../components/utils/TablePaginationActions';
import useTablePaginationInit from '../../../../../components/hooks/useTablePaginationInit';
import TableTimeStamps from '../../../../../components/utils/TableTimeStamps';
import TopBar from '../../../../../components/utils/TopBar';
import NA from '../../../../../components/utils/NA';

import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';

import CodeViewModal from '../../../../../components/modals/CodeViewModal';

import {
  GetScNodeContentBIZcoreTD
} from '../../../../../redux/actions/business/core/scNodeContentActions';

const ScNodeContentPage = () => {
  const dispatch = useDispatch();
  const state = useSelector(state => state["business.core.tradeDefence"]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");

  const [contentList, setContentList] = useState([]);
  const [contentTotal, setContentTotal] = useState(0);

  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewModalTitle, setViewModalTitle] = useState("");
  const [viewModalContent, setViewModalContent] = useState("");

  const [
    page,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage
  ] = useTablePaginationInit();

  // This table is content-addressed: a row IS its text, keyed by text_hash.
  // Where that text sits and which revision reads it lives on SC Node Revision
  // Status, so none of those columns belong here.
  const tableHeadingsList = [
    { label: "Text hash", sort: "text_hash" },
    { label: "Raw legal text", sort: null },
    { label: "Characters", sort: null },
    { label: "Used by", sort: null },
    { label: "First seen", sort: "first_seen_at" },
    { label: "", sort: null },
  ];

  const handleSort = (value) => {
    if (!value) return;
    setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    setSort(value);
  };

  useEffect(() => {
    handleGetContent();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, search, sort, sortDirection]);

  useEffect(() => {
    try {
      setContentList(state.scNodeContentList.records || []);
      setContentTotal(state.scNodeContentList.total || 0);
    } catch (err) {}
  }, [state]);

  async function handleGetContent(customLoading) {
    let query = "";
    if (search) query += `&search=${search}`;
    if (sort) query += `&ordering=${sortDirection === "asc" ? '' : '-'}${sort}`;
    dispatch(GetScNodeContentBIZcoreTD(page + 1, rowsPerPage, query, true, customLoading ? customLoading : setLoading));
  };

  const truncate = (text, maxLen = 80) => {
    if (!text) return null;
    return text.length > maxLen ? text.substring(0, maxLen) + "..." : text;
  };

  const handleViewRawText = (row) => {
    setViewModalTitle(row.text_hash ? `Content ${row.text_hash.substring(0, 12)}` : 'Raw Legal Text');
    setViewModalContent(row.raw_legal_text || '');
    setViewModalOpen(true);
  };

  return (
    <Box>

      <CodeViewModal
        open={viewModalOpen}
        setOpen={setViewModalOpen}
        title={viewModalTitle}
        language="text"
        content={viewModalContent}
      />

      <TopBar
        title={"SC Node Content"}
        search={search}
        onSearchChange={(e) => setSearch(e.target.value)}
      />

      {
        loading
        ?
        <Box height={"300px"} className="flexCenterCenterRow">
          <CircularProgress />
        </Box>
        :
          contentList.length === 0
          ?
          <Box height={"300px"} className="flexCenterCenterRow">
            <Typography>No SC node content found</Typography>
          </Box>
          :
          <Table className="block">
            <TableHead>
              <TableRow>
                {
                  tableHeadingsList.map((heading, headingIndex) => (
                    heading.sort === null
                    ?
                    <TableCell key={headingIndex}>{heading.label}</TableCell>
                    :
                    <TableCell key={headingIndex}>
                      <TableSortLabel
                        active={sort === heading.sort}
                        direction={sortDirection}
                        onClick={() => handleSort(heading.sort)}
                      >
                        {heading.label}
                      </TableSortLabel>
                    </TableCell>
                  ))
                }
              </TableRow>
            </TableHead>
            <TableBody>
              {
                contentList.map((row, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      {row.text_hash
                        ? <Tooltip title={row.text_hash} placement="top">
                            <Typography variant='caption' fontWeight={600} fontFamily="monospace">
                              {truncate(row.text_hash, 16)}
                            </Typography>
                          </Tooltip>
                        : <NA />}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 420 }}>
                      <Typography variant='caption' color={"grey"} sx={{ wordBreak: 'break-word' }}>
                        {truncate(row.raw_legal_text, 140) || <NA />}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant='caption' color={"grey"}>
                        {row.char_count != null ? row.char_count.toLocaleString() : <NA />}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {/* How many (node, revision) placements read this text.
                          Anything above 1 is text this schema stores only once. */}
                      <Chip
                        size="small"
                        label={`${row.used_by_revisions ?? 0} placement${row.used_by_revisions === 1 ? '' : 's'}`}
                        color={row.used_by_revisions > 1 ? 'primary' : 'primary'}
                        variant={row.used_by_revisions > 1 ? 'filled' : 'outlined'}
                        sx={{ height: 20, fontSize: 11 }}
                      />
                    </TableCell>
                    <TableCell>
                      <TableTimeStamps createdAt={row.first_seen_at} />
                    </TableCell>
                    <TableCell>
                      <Tooltip title="View raw text">
                        <IconButton size="small" color="primary" onClick={() => handleViewRawText(row)}>
                          <VisibilityOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              }
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  sx={{ borderBottom: 0 }}
                  rowsPerPageOptions={[5, 10, 25, 50, 100]}
                  colSpan={6}
                  count={contentTotal}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  SelectProps={{
                    inputProps: { 'aria-label': 'rows per page' },
                    native: true
                  }}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                />
              </TableRow>
            </TableFooter>
          </Table>
      }

    </Box>
  );
};

export default ScNodeContentPage;
